import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
} from "@mui/material";

const PaymentDetailsModal = ({ open, onClose, booking, eventType = "tech_camp" }) => {
  if (!booking) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const normalizedType = (booking.event_type || eventType || "").toLowerCase();
  const isFDP = normalizedType === "fdp";

  const domainTitle = isFDP ? "FDP Domain" : "Tech Camp Domain";
  const durationTitle = isFDP ? "FDP Duration" : "Tech Camp Duration";

  const programFee = isFDP ? "₹30,000" : "₹10,000";
  const gstAmount = isFDP ? "₹5,400" : "₹1,800";
  const totalAmount = isFDP ? "₹35,400" : "₹11,800";

  const dateDisplay =
    booking.start_date && booking.end_date
      ? `${formatDate(booking.start_date)} to ${formatDate(booking.end_date)}`
      : booking.date_range || "-";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 4,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "#4077f0",
          color: "#fff",
          fontWeight: 600,
          fontSize: 16,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          pb: 3,
          pt: 3,
          px: 3,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 18,
            flexShrink: 0,
            border: "2px solid rgba(255, 255, 255, 0.4)",
          }}
        >
          💳
        </Box>
        <Box>
          <Typography fontSize={16} fontWeight={700} sx={{ color: "#fff" }}>
            Payment Details
          </Typography>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          py: 4,
          px: 4,
          pt: 3.5,
          background: "linear-gradient(to bottom, #FAFBFC 0%, #F5F7FA 100%)",
        }}
      >
        {/* Booking Details */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              backgroundColor: "#FFFFFF",
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: 2,
                mb: 1,
              }}
            >
              <Typography fontSize={12} color="#94A3B8">
                {domainTitle}
              </Typography>
              <Typography fontSize={12} color="#94A3B8">
                {durationTitle}
              </Typography>
            </Box>

            {/* Value Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: 2,
              }}
            >
              <Typography
                fontSize={14}
                fontWeight={600}
                color="#1E293B"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {booking.domain_name || booking.domain || "-"}
              </Typography>

              <Typography fontSize={14} fontWeight={600} color="#1E293B">
                {dateDisplay}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Payment Details */}
        <Box sx={{ mb: 2 }}>
          <Typography
            fontSize={13}
            fontWeight={600}
            color="#475569"
            sx={{ mb: 1.5 }}
          >
            Bank Account Details
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography fontSize={12} color="#94A3B8">
                Account Name
              </Typography>
              <Typography fontSize={14} fontWeight={600}>
                EDUSKILLS FOUNDATION
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography fontSize={12} color="#94A3B8">
                Bank Name
              </Typography>
              <Typography fontSize={14} fontWeight={600}>
                AXIS BANK
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography fontSize={12} color="#94A3B8">
                Account Number
              </Typography>
              <Typography
                fontSize={14}
                fontWeight={600}
                sx={{ fontFamily: "monospace" }}
              >
                922010056667723
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography fontSize={12} color="#94A3B8">
                IFSC Code
              </Typography>
              <Typography
                fontSize={14}
                fontWeight={600}
                sx={{ fontFamily: "monospace" }}
              >
                UTIB0000381
              </Typography>
            </Box>

            {/* Payment Summary */}
            <Box sx={{ mt: 2 }}>
              <Typography fontSize={12} color="#94A3B8" sx={{ mb: 1 }}>
                Payment Summary
              </Typography>

              <Box
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid #E5E7EB",
                  overflow: "hidden",
                }}
              >
                {/* Program Fee Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1.2,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography fontSize={13} color="#475569">
                    Program Fee
                  </Typography>
                  <Typography fontSize={13} fontWeight={600} color="#1E293B">
                    {programFee}
                  </Typography>
                </Box>

                {/* GST Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1.2,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography fontSize={13} color="#475569">
                    Taxes (18% GST)
                  </Typography>
                  <Typography fontSize={13} fontWeight={600} color="#1E293B">
                    {gstAmount}
                  </Typography>
                </Box>

                {/* Total Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1.4,
                    backgroundColor: "#FEF3C7",
                  }}
                >
                  <Typography fontSize={13} fontWeight={700} color="#92400E">
                    Total Payable Amount
                  </Typography>
                  <Typography fontSize={15} fontWeight={800} color="#DC2626">
                    {totalAmount}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Note */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mt: 2,
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: "#FFF7ED",
                border: "1px solid #FED7AA",
              }}
            >
              <Typography fontSize={16} sx={{ lineHeight: 1, mt: "1px" }}>
                📌
              </Typography>
              <Typography fontSize={12} color="#92400E" fontWeight={500}>
                <b>Note:</b> Your slot will be confirmed after payment verification.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "2px solid #E0E7FF",
          backgroundColor: "#F8FAFC",
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#CBD5E1",
            color: "#475569",
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDetailsModal;
